import { useEffect } from 'react'
import { AnimatePresence } from 'framer-motion'

import { HOTKEYS } from './helpers'
import {
  Backdrop,
  Card,
  Header,
  Title,
  Subtitle,
  HotkeyList,
  HotkeyRow,
  KeysWrap,
  Key,
  Description,
  CloseHint,
} from './styled'
import { HotkeysCheatsheetProps } from './types'

/**
 * Modal that lists every keyboard shortcut available in the app. Opens
 * on `?` press, closes on Esc / backdrop click. Pure reference — no
 * actions, just a discoverable cheatsheet.
 *
 * @param {HotkeysCheatsheetProps} props
 * @returns {JSX.Element | null}
 */
const HotkeysCheatsheet = ({
  isOpen,
  onClose,
}: HotkeysCheatsheetProps): JSX.Element | null => {
  useEffect(() => {
    if (isOpen === false) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  const stopPropagation = (event: { stopPropagation: () => void }): void => {
    event.stopPropagation()
  }

  if (isOpen === false) {
    return null
  }

  return (
    <AnimatePresence>
      <Backdrop
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Card
          initial={{ opacity: 0, y: -16, scale: 0.97 }}
          animate={{
            opacity: 1,
            y: 0,
            scale: 1,
            transition: { duration: 0.22 },
          }}
          exit={{ opacity: 0, y: -16, scale: 0.97 }}
          onClick={stopPropagation}
        >
          <Header>
            <Title>Keyboard shortcuts</Title>
            <Subtitle>Press ? any time</Subtitle>
          </Header>

          <HotkeyList>
            {HOTKEYS.map(entry => (
              <HotkeyRow key={entry.description}>
                <Description>{entry.description}</Description>
                <KeysWrap>
                  {entry.keys.map(key => (
                    <Key key={key}>{key}</Key>
                  ))}
                </KeysWrap>
              </HotkeyRow>
            ))}
          </HotkeyList>

          <CloseHint>Esc or click outside to close</CloseHint>
        </Card>
      </Backdrop>
    </AnimatePresence>
  )
}

export default HotkeysCheatsheet
